/**
 * The three ways `leads.html` shows the same rows: table, split and kanban.
 *
 * Split out of `LeadsListPage.jsx` because the page is already the filter state,
 * the saved views, the toolbar and the footer; the views are pure rendering plus
 * one mutation each, and reading them next to the prototype is easier when they
 * are not buried in three hundred lines of query wiring.
 *
 * All three take rows already normalised by `toRow()`, so none of them knows
 * whether the data came from the API or the fixture.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Avatar, EmptyState } from '../../components/common/index.jsx';
import { SkeletonTable } from '../../components/common/Loading.jsx';
import { money, moneyShort } from '../../utils/formatters.js';
import { STATUS_PILL, scoreClass, BOARD_COLUMNS } from './leadShape.js';

/* -------------------------------------------------------------------------- */
/* Shared cell fragments                                                      */
/* -------------------------------------------------------------------------- */

const Dash = () => <span className="ink-4">—</span>;

/** The prototype's amber Queue pill, or an avatar and a name. */
function OwnerCell({ name }) {
  if (!name) {
    return (
      <span className="pill pill-amber">
        <i className="bi bi-inbox" aria-hidden="true" />Queue
      </span>
    );
  }
  return (
    <div className="d-flex align-items-center gap-2">
      <Avatar name={name} size={22} />
      <span className="t-xs no-wrap">{name}</span>
    </div>
  );
}

/**
 * Follow-up, in the prototype's three states.
 *
 * "Not set" is amber rather than grey on purpose — a lead nobody has scheduled
 * is a lead being dropped, which is the same category of problem as an overdue
 * one, not the absence of a problem.
 */
function FollowupCell({ row }) {
  if (!row.followupLabel) return <span className="pill pill-amber">Not set</span>;
  if (row.overdue) return <span className="pill pill-rose">{row.followupLabel}</span>;
  return <span className="t-xs ink-2 no-wrap">{row.followupLabel}</span>;
}

const ScoreChip = ({ score }) => (
  <span className={`score ${scoreClass(score)}`}>{score ?? '—'}</span>
);

/* -------------------------------------------------------------------------- */
/* Inline editing                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The prototype's `.ie` pencil-to-input cell.
 *
 * Edits are held, not sent: the pencil writes into the page's `edits` map and
 * the save bar applies them. That is the prototype's behaviour and it is also
 * the right one here, because each save is a separate PATCH against a remote
 * database — committing on blur would mean a round trip per keystroke-and-tab
 * and no way to change your mind about three cells at once.
 *
 * `options` turns it into a select. `format` renders the committed value the way
 * the column renders everything else, so a saved status still looks like a pill.
 */
export function InlineCell({
  row, field, value, display, options, disabled, disabledHint, edits, onEdit, format,
}) {
  const key = `${row.id}.${field}`;
  const pending = edits.get(key);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    if (inputRef.current?.select) inputRef.current.select();
  }, [editing]);

  const current = pending !== undefined ? pending : value;
  const shown = pending !== undefined ? (format ? format(pending) : pending) : display;

  if (!row.editable || disabled) {
    /* No pencil at all rather than a disabled one. A control that is visible
       but refuses is a question the user has to ask someone; nothing there is
       an answer they can read from the title attribute if they wonder. */
    return <span title={disabled ? disabledHint : undefined}>{shown}</span>;
  }

  if (editing) {
    const commit = (next) => {
      setEditing(false);
      if (String(next) === String(value ?? '')) onEdit(key, undefined);
      else onEdit(key, next);
    };

    if (options) {
      return (
        <select
          ref={inputRef}
          className="ie-input"
          defaultValue={current ?? ''}
          onBlur={(e) => commit(e.target.value)}
          onChange={(e) => e.target.blur()}
          onKeyDown={(e) => { if (e.key === 'Escape') { setEditing(false); } }}
          aria-label={`${field} for ${row.name}`}
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }

    return (
      <input
        ref={inputRef}
        className="ie-input"
        defaultValue={current ?? ''}
        size={Math.max(10, String(current ?? '').length)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { setEditing(false); }
        }}
        aria-label={`${field} for ${row.name}`}
      />
    );
  }

  return (
    <span className={`ie ${pending !== undefined ? 'ie-dirty' : ''}`}>
      <span className="ie-val">{shown}</span>
      <button
        type="button"
        className="ie-pencil"
        title={`Edit ${field}`}
        onClick={() => setEditing(true)}
      >
        <i className="bi bi-pencil-fill" aria-hidden="true" />
      </button>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Table view                                                                 */
/* -------------------------------------------------------------------------- */

/** The prototype's twelve columns, and the skeleton's, from one list. */
export const TABLE_COLUMNS = [
  { key: '__sel', header: '', width: 34 },
  { key: 'name', header: 'Name' },
  { key: 'company', header: 'Company' },
  { key: 'title', header: 'Title' },
  { key: 'phone', header: 'Phone' },
  { key: 'source', header: 'Lead Source' },
  /* Not one of the prototype's twelve — docs/modules/partnerLeads.model.md.
     ColumnsModal picks it up automatically since it just reads this array,
     same as any other column — visible by default like the rest. */
  { key: 'partner', header: 'Partner', width: 140 },
  { key: 'score', header: 'Score', width: 72 },
  { key: 'status', header: 'Lead Status', width: 124 },
  { key: 'value', header: 'Est. Value', width: 110, align: 'end' },
  { key: 'owner', header: 'Owner', width: 160 },
  { key: 'followup', header: 'Next Follow-up', width: 130 },
  { key: '__menu', header: '', width: 44 },
];

/**
 * @param {{columns?:Array}} props `columns` is TABLE_COLUMNS minus whatever the
 *   reader has hidden. Every cell below is keyed off it rather than rendered
 *   unconditionally, so hiding Phone removes the `<th>` and the `<td>` together
 *   — a table whose header and body disagree about how many cells there are is
 *   a table with the wrong data under the wrong heading.
 */
export function LeadTable({
  rows, isLoading, selected, onToggle, onToggleAll, edits, onEdit, columns = TABLE_COLUMNS,
  statusOptions, canEdit, canDelete, canAssign, onDelete, onChangeOwner, onEmail, onNew, filtered, sort, onSort,
}) {
  const shown = new Set(columns.map((c) => c.key));
  if (isLoading) return <SkeletonTable rows={10} columns={columns} />;

  if (!rows.length) {
    return (
      <div className="empty">
        <div className="e-ico"><i className="bi bi-magnet" aria-hidden="true" /></div>
        <div className="t-md fw-650">No leads in this view</div>
        <div className="t-sm ink-3">
          {filtered ? 'Try another list view, or widen the filters.' : 'Create a lead to get started.'}
        </div>
        {onNew && (
          <button type="button" className="btn btn-add btn-sm-x mt-3" onClick={onNew}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />New Lead
          </button>
        )}
      </div>
    );
  }

  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));

  /* Only the columns the API can actually order by. Clicking a header that
     silently does nothing is worse than a header that is plainly not a button. */
  const SORTABLE = {
    name: 'full_name', company: 'company_name', score: 'score',
    value: 'estimated_value', followup: 'next_followup_at',
  };

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 34 }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll(!allSelected)}
                aria-label="Select every lead on this page"
              />
            </th>
            {columns.filter((c) => c.key !== '__sel' && c.key !== '__menu').map((c) => {
              const field = SORTABLE[c.key];
              const active = field && (sort === field || sort === `-${field}`);
              const desc = sort === `-${field}`;
              return (
                <th key={c.key} className={c.align === 'end' ? 'num' : ''} style={c.width ? { width: c.width } : undefined}>
                  {field ? (
                    <button
                      type="button"
                      className="btn btn-ghost p-0 border-0 t-inherit"
                      style={{ font: 'inherit', color: 'inherit' }}
                      onClick={() => onSort(active && !desc ? `-${field}` : field)}
                    >
                      {c.header}
                      {active && (
                        <i
                          className={`bi ${desc ? 'bi-arrow-down-short' : 'bi-arrow-up-short'}`}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  ) : c.header}
                </th>
              );
            })}
            <th style={{ width: 44 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={row.converted ? 'is-converted' : undefined}>
              <td>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={selected.includes(row.id)}
                  onChange={() => onToggle(row.id)}
                  aria-label={`Select ${row.name}`}
                />
              </td>

              <td>
                <div className="d-flex align-items-center gap-2">
                  <Avatar name={row.name} size={27} />
                  <div className="min-w-0">
                    <Link to={`/leads/${row.id}`} className="strong d-block text-truncate">{row.name}</Link>
                    <div className="t-2xs ink-4">
                      {row.lead_no}{row.converted ? ' · converted' : ''}
                    </div>
                  </div>
                </div>
              </td>

{shown.has('company') && (
              <td className="no-wrap">
                <InlineCell
                  row={row} field="company_name" value={row.company}
                  display={row.company ?? <Dash />}
                  disabled={!canEdit} disabledHint="Needs leads:edit"
                  edits={edits} onEdit={onEdit}
                />
              </td>
              )}

{shown.has('title') && (
              <td className="t-xs ink-3 no-wrap">
                <InlineCell
                  row={row} field="job_title" value={row.title}
                  display={row.title ?? <Dash />}
                  disabled={!canEdit} disabledHint="Needs leads:edit"
                  edits={edits} onEdit={onEdit}
                />
              </td>
              )}

{shown.has('phone') && (
              <td className="t-xs no-wrap">
                <InlineCell
                  row={row} field="phone" value={row.phone}
                  display={row.phone ?? <Dash />}
                  disabled={!canEdit} disabledHint="Needs leads:edit"
                  edits={edits} onEdit={onEdit}
                />
              </td>
              )}

{shown.has('source') && (
              <td>{row.source ? <span className="pill pill-gray">{row.source}</span> : <Dash />}</td>
              )}

{shown.has('partner') && (
              <td>{row.partner ? <span className="pill pill-gray">{row.partner}</span> : <Dash />}</td>
              )}

{shown.has('score') && (
              <td><ScoreChip score={row.score} /></td>
              )}

              {shown.has('status') && (
              <td>
                <InlineCell
                  row={row} field="status_id"
                  value={row.raw.status?.id}
                  display={row.status
                    ? <span className={`pill ${STATUS_PILL[row.status] ?? 'pill-gray'}`}>{row.status}</span>
                    : <Dash />}
                  options={statusOptions}
                  format={(id) => {
                    const name = statusOptions.find((o) => o.value === id)?.label;
                    return <span className={`pill ${STATUS_PILL[name] ?? 'pill-gray'}`}>{name}</span>;
                  }}
                  disabled={!canEdit} disabledHint="Needs leads:edit"
                  edits={edits} onEdit={onEdit}
                />
              </td>
              )}

              {shown.has('value') && (
              <td className="num strong">
                <InlineCell
                  row={row} field="estimated_value" value={row.value}
                  display={row.value ? moneyShort(row.value, row.currency) : <Dash />}
                  format={(v) => moneyShort(Number(v), row.currency)}
                  disabled={!canEdit} disabledHint="Needs leads:edit"
                  edits={edits} onEdit={onEdit}
                />
              </td>

              )}

              {/* No pencil. `PATCH /leads/{id}` omits `owner_id` by design —
                  §4: "Assignment is its own permission, its own audit action and
                  usually notifies someone." The endpoint that would do it,
                  POST /leads/{id}/assign, answers 501. */}
              {shown.has('owner') && <td><OwnerCell name={row.owner} /></td>}

{shown.has('followup') && (
              <td><FollowupCell row={row} /></td>
              )}

              <td className="text-end">
                <RowMenu
                  row={row}
                  canDelete={canDelete}
                  canAssign={canAssign}
                  onDelete={onDelete}
                  onChangeOwner={onChangeOwner}
                  onEmail={onEmail}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The per-row caret menu.
 *
 * Bootstrap's dropdown JS is not loaded — only its CSS — so `.show` is toggled
 * here and a document listener closes it. Entries whose endpoint answers 501
 * are rendered `disabled` with the reason in the title rather than omitted:
 * `leads.html` offers Convert and Clone, and a reader comparing the two screens
 * should be able to see that they are known about and pending, not forgotten.
 * Change Owner is the exception — it is real (`POST /leads/{id}/assign`,
 * gated `leads:assign`), so it is a live item rather than a disabled one, and
 * only rendered at all when `canAssign` is true rather than shown disabled.
 * Send Email is the other exception now that §8 is real — `onEmail` opens
 * `EmailComposerModal` anchored to this one lead.
 */
function RowMenu({ row, canDelete, canAssign, onDelete, onChangeOwner, onEmail }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const box = useRef(null);
  const btn = useRef(null);
  const menu = useRef(null);

  /* The menu is rendered in a portal with `position: fixed`, not inside the row.
     The table sits in `.tbl-wrap` (overflow-x: auto), which forces overflow-y to
     clip as well, so an absolutely positioned menu on the last rows was cut off at
     the bottom of the table. Placed from the button's rect after it has been
     measured: below the button when it fits, flipped above it when it doesn't. */
  useLayoutEffect(() => {
    if (!open || !btn.current || !menu.current) return;
    const b = btn.current.getBoundingClientRect();
    const h = menu.current.offsetHeight;
    const w = menu.current.offsetWidth;
    const gap = 6;
    const fitsBelow = b.bottom + gap + h <= window.innerHeight - 8;
    const top = fitsBelow ? b.bottom + gap : Math.max(8, b.top - gap - h);
    const left = Math.min(Math.max(8, b.right - w), window.innerWidth - w - 8);
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => { setOpen(false); setPos(null); };
    const away = (e) => {
      if (!box.current?.contains(e.target) && !menu.current?.contains(e.target)) close();
    };
    const esc = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    // A scrolled page would leave a fixed menu floating away from its row.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const notBuilt = (what, section) => (
    <li>
      <span className="dropdown-item disabled" title={`${what} is docs/API.md ${section} and answers 501.`}>
        <i className="bi bi-slash-circle me-2" aria-hidden="true" />{what}
      </span>
    </li>
  );

  return (
    <div className="dropdown d-inline-block" ref={box}>
      <button
        ref={btn}
        type="button"
        className="icon-btn"
        style={{ width: 28, height: 28, fontSize: 13 }}
        onClick={() => { setPos(null); setOpen((v) => !v); }}
        aria-expanded={open}
        aria-label={`Actions for ${row.name}`}
      >
        <i className="bi bi-caret-down-fill" style={{ fontSize: 10 }} aria-hidden="true" />
      </button>
      {open && createPortal(
      <ul
        ref={menu}
        className="dropdown-menu show"
        /* Hidden until measured, so it never flashes at the wrong spot. */
        style={{
          position: 'fixed', margin: 0, right: 'auto', zIndex: 1060,
          top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden',
        }}
      >
        <li>
          <Link className="dropdown-item" to={`/leads/${row.id}`} onClick={() => setOpen(false)}>
            <i className="bi bi-eye me-2" aria-hidden="true" />View
          </Link>
        </li>
        {/* Conversion happens on the lead's own page (it needs the account / contact /
            opportunity details), so this opens it — and only offers it for a Qualified lead. */}
        <li>
          {row.status === 'Qualified' && !row.converted ? (
            <Link className="dropdown-item" to={`/leads/${row.id}`} onClick={() => setOpen(false)}>
              <i className="bi bi-arrow-right-circle me-2" aria-hidden="true" />Convert…
            </Link>
          ) : (
            <span className="dropdown-item disabled" title="Only a Qualified lead can be converted.">
              <i className="bi bi-arrow-right-circle me-2" aria-hidden="true" />Convert
            </span>
          )}
        </li>
        <li>
          <button type="button" className="dropdown-item" onClick={() => { setOpen(false); onEmail(row); }}>
            <i className="bi bi-envelope me-2" aria-hidden="true" />Send Email
          </button>
        </li>
        {canAssign && (
          <li>
            <button
              type="button"
              className="dropdown-item"
              onClick={() => { setOpen(false); onChangeOwner(row); }}
            >
              <i className="bi bi-person-check me-2" aria-hidden="true" />Change Owner
            </button>
          </li>
        )}
        {notBuilt('Clone', '§4')}
        <li><hr className="dropdown-divider" /></li>
        <li>
          <button
            type="button"
            className="dropdown-item danger"
            disabled={!canDelete}
            title={canDelete ? undefined : 'Needs leads:delete'}
            onClick={() => { setOpen(false); onDelete(row); }}
          >
            <i className="bi bi-trash me-2" aria-hidden="true" />Delete
          </button>
        </li>
      </ul>,
      document.body,
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Split view                                                                 */
/* -------------------------------------------------------------------------- */

/** The lifecycle the preview's path renders. Converted is the end of it. */
const LIFECYCLE = ['New', 'Working', 'Nurturing', 'Qualified', 'Converted'];

export function SplitView({ rows, isLoading, activeId, onSelect, onDelete, canDelete, onEmail }) {
  if (isLoading) return <SkeletonTable rows={8} columns={TABLE_COLUMNS.slice(1, 6)} />;
  if (!rows.length) {
    return <EmptyState icon="bi-magnet" title="No leads in this view" message="Try another list view, or widen the filters." />;
  }

  const active = rows.find((r) => r.id === activeId) ?? rows[0];

  return (
    <div className="splitview">
      <div className="sv-list">
        {rows.map((row) => (
          <div
            key={row.id}
            className={`sv-item ${row.id === active.id ? 'active' : ''}`}
            onClick={() => onSelect(row.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(row.id); } }}
            role="button"
            tabIndex={0}
          >
            <div className="d-flex align-items-center gap-2">
              <Avatar name={row.name} size={27} />
              <div className="min-w-0 flex-grow-1">
                <div className="n text-truncate">{row.name}</div>
                <div className="c text-truncate">{row.company}</div>
              </div>
              <ScoreChip score={row.score} />
            </div>
            <div className="d-flex align-items-center gap-1 mt-2">
              <span className={`pill ${STATUS_PILL[row.status] ?? 'pill-gray'}`}>{row.status}</span>
              <span className="t-2xs ink-4 ms-auto">{row.followupLabel ?? 'No follow-up'}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="sv-detail">
        <LeadPreview row={active} onDelete={onDelete} canDelete={canDelete} onEmail={onEmail} />
      </div>
    </div>
  );
}

function LeadPreview({ row, onDelete, canDelete, onEmail }) {
  const reached = LIFECYCLE.indexOf(row.status);

  return (
    <div className="p-3">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Avatar name={row.name} size={42} />
        <div className="min-w-0">
          <div className="t-md fw-700">{row.name}</div>
          <div className="t-xs ink-3">{[row.title, row.company].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="ms-auto d-flex gap-1">
          {row.phone && (
            <a href={`tel:${row.phone.replace(/\s/g, '')}`} className="btn btn-soft btn-sm-x" title={`Call ${row.phone}`}>
              <i className="bi bi-telephone" aria-hidden="true" />
            </a>
          )}
          {row.email && (
            <button type="button" className="btn btn-soft btn-sm-x" title={`Email ${row.email}`} onClick={() => onEmail(row)}>
              <i className="bi bi-envelope" aria-hidden="true" />
            </button>
          )}
          <Link to={`/leads/${row.id}`} className="btn btn-brand btn-sm-x">Open record</Link>
          {canDelete && (
            <button type="button" className="btn btn-soft btn-sm-x text-danger" title="Delete" onClick={() => onDelete(row)}>
              <i className="bi bi-trash" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* The prototype's own tel/mailto links were dead; Call is a real `tel:`
          link since the browser already knows how to place one, and Email
          opens EmailComposerModal (§8 is real now) rather than the OS mail
          client, so the send is tracked on this lead's own activity timeline. */}

      <ul className="path mb-3">
        {LIFECYCLE.map((step, i) => (
          <li key={step}>
            <span className={`step ${reached > i ? 'done' : reached === i ? 'current' : ''}`}>
              {reached > i && <i className="bi bi-check-lg" aria-hidden="true" />}{step}
            </span>
          </li>
        ))}
      </ul>

      <div className="row g-3">
        <div className="col-md-6">
          <PreviewField k="Email" v={row.email} />
          <PreviewField k="Phone" v={row.phone} />
          <PreviewField k="Lead Source" v={row.source} />
          <PreviewField k="Industry" v={row.industry} />
          <PreviewField k="Country" v={row.country} />
        </div>
        <div className="col-md-6">
          <div className="field-row">
            <div className="fk">Rating</div>
            <div className="fv d-flex align-items-center gap-2">
              <ScoreChip score={row.score} />{row.rating ?? '—'}
            </div>
          </div>
          <PreviewField k="Est. Value" v={row.value ? money(row.value, row.currency) : null} />
          <PreviewField k="Owner" v={row.owner ?? 'Unassigned queue'} />
          <PreviewField k="Next Follow-up" v={row.followupLabel} />
          <PreviewField k="Employees" v={row.employees?.toLocaleString()} />
        </div>
      </div>

      {row.engagement && (
        <>
          <div className="eyebrow mt-4 mb-2">Engagement</div>
          <div className="d-flex flex-wrap gap-2">
            <span className="pill pill-gray">{row.engagement.email_opens} opens</span>
            <span className="pill pill-gray">{row.engagement.email_replies} replies</span>
            <span className="pill pill-gray">{row.engagement.website_visits} visits</span>
            <span className="pill pill-gray">{row.engagement.meetings_held} meetings</span>
          </div>
          <div className="t-2xs ink-4 mt-2">
            These counters are what the scoring rules read — see the score panel on the record.
          </div>
        </>
      )}
    </div>
  );
}

const PreviewField = ({ k, v }) => (
  <div className="field-row">
    <div className="fk">{k}</div>
    <div className="fv">{v || <Dash />}</div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Kanban view                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Status board, with drag-and-drop that really writes.
 *
 * A drop is `PATCH /leads/{id}` with the target `status_id` — the one bulk-ish
 * gesture on this screen that the API fully supports, since status is patchable
 * and ownership is not. There is no optimistic move: the card stays where it is
 * until the write comes back, because at ~1.3s per round trip an optimistic
 * card that springs back on a 409 is worse than one that visibly waits.
 *
 * @param {{truncated:boolean}} props `truncated` when the board is showing a
 *   capped page rather than everything — the column counts are then a floor,
 *   and saying so is the difference between an incomplete board and a wrong one.
 */
export function KanbanView({
  rows, isLoading, statusOptions, onMove, moving, canEdit, truncated, total,
}) {
  /**
   * Two places for the dragged id, and both are needed.
   *
   * `dragRef` is what the drop reads. `dragstart` and `drop` can land in the
   * same React batch — they do whenever the events arrive faster than a render,
   * which is every synthetic drag and any fast real one — and in that case the
   * `drop` handler still closes over the *previous* render's `dragId`, which is
   * null, so the card silently does not move. A ref is current the instant it
   * is written.
   *
   * `dragId` state exists only to put `.dragging` on the card, which is a
   * render concern and so has to be state.
   */
  const dragRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [overStatus, setOverStatus] = useState(null);

  if (isLoading) {
    return (
      <div className="p-3">
        <div className="kanban">
          {BOARD_COLUMNS.map(([name, colour]) => (
            <div className="kan-col" key={name}>
              <div className="stage-line" style={{ background: colour }} />
              <div className="kan-head">
                <span className="name">{name}</span>
              </div>
              <div className="kan-body">
                <SkeletonTable rows={2} columns={[{ key: 'c', header: '' }]} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const idFor = (name) => statusOptions.find((o) => o.label === name)?.value;

  return (
    <div className="p-3">
      {truncated && (
        <div className="t-xs ink-3 mb-2">
          <i className="bi bi-info-circle me-1" aria-hidden="true" />
          Showing the first {rows.length} of {total} leads — the board asks for one
          page of 200, the API&rsquo;s maximum. Narrow the view to see all of it.
        </div>
      )}

      <div className="kanban">
        {BOARD_COLUMNS.map(([name, colour]) => {
          const items = rows.filter((r) => r.status === name);
          const sum = items.reduce((a, r) => a + (Number(r.value) || 0), 0);
          const targetId = idFor(name);

          return (
            <div className="kan-col" key={name}>
              <div className="stage-line" style={{ background: colour }} />
              <div className="kan-head">
                <div className="d-flex align-items-center justify-content-between">
                  <span className="name">{name}</span>
                  <span className="pill pill-gray">{items.length}</span>
                </div>
                <div className="meta mt-1">{moneyShort(sum)} estimated</div>
              </div>

              <div
                className={`kan-body ${overStatus === name ? 'drag-over' : ''}`}
                onDragOver={(e) => {
                  if (!canEdit || !targetId) return;
                  e.preventDefault();
                  setOverStatus(name);
                }}
                onDragLeave={() => setOverStatus((s) => (s === name ? null : s))}
                onDrop={(e) => {
                  e.preventDefault();
                  setOverStatus(null);
                  /* `dataTransfer` first — it is the standard channel and it
                     survives a drag between windows — then the ref, because a
                     synthetic DragEvent may carry no data. */
                  const id = e.dataTransfer?.getData('text/plain') || dragRef.current;
                  const row = rows.find((r) => r.id === id);
                  dragRef.current = null;
                  setDragId(null);
                  if (row && targetId && row.status !== name) onMove(row, targetId, name);
                }}
              >
                {items.map((row) => (
                  <div
                    key={row.id}
                    className={`lead-card ${dragId === row.id ? 'dragging' : ''} ${moving === row.id ? 'lead-card-busy' : ''}`}
                    draggable={canEdit && moving === null}
                    onDragStart={(e) => {
                      dragRef.current = row.id;
                      e.dataTransfer?.setData('text/plain', row.id);
                      setDragId(row.id);
                    }}
                    onDragEnd={() => { dragRef.current = null; setDragId(null); setOverStatus(null); }}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <Avatar name={row.name} size={27} />
                      <div className="min-w-0 flex-grow-1">
                        <Link to={`/leads/${row.id}`} className="t-sm fw-650 ink d-block text-truncate">
                          {row.name}
                        </Link>
                        <div className="t-xs ink-3 text-truncate">{row.company}</div>
                      </div>
                      <ScoreChip score={row.score} />
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-2">
                      <span className="t-sm fw-750 tabular">
                        {row.value ? moneyShort(row.value, row.currency) : '—'}
                      </span>
                      {row.source && <span className="pill pill-gray">{row.source}</span>}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-2">
                      {row.owner
                        ? <Avatar name={row.owner} size={22} />
                        : (
                          /* Inline, not a tone class: `.av-1`…`.av-6` are the
                             six hashed tints for real people, and the queue is
                             not a person. */
                          <span
                            className="av av-xs"
                            style={{ background: 'var(--surface-3)', color: 'var(--ink-3)' }}
                            title="Unassigned queue"
                          >?</span>
                        )}
                      <span className={`t-2xs ${row.overdue ? 'text-danger fw-650' : 'ink-4'}`}>
                        <i className="bi bi-calendar3 me-1" aria-hidden="true" />
                        {row.followupLabel ?? 'Not set'}
                      </span>
                    </div>
                  </div>
                ))}

                {items.length === 0 && (
                  <div className="t-xs ink-4 text-center py-3">
                    {canEdit ? 'Drop a lead here' : 'Nothing here'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="t-2xs ink-4 mt-2">
        {canEdit
          ? 'Dragging a card writes the new status straight away — PATCH /leads/{id}.'
          : 'Read-only: moving a card needs leads:edit.'}
        {' '}Converted and Junk have no column, because the board is for leads still being worked.
      </div>
    </div>
  );
}
